import { useState, useEffect } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import ErrorBanner from "../components/ErrorBanner";
import { AFE_CATEGORIES } from "../utils/afe";
import ExpenseDivisionPicker, { EMPTY_DIVISION_TAG, divisionBody, splitProblem } from "../components/ExpenseDivisionPicker";

// ─── RESPONSIVE HOOK ─────────────────────────────────────
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, []);
  return isMobile;
}

// Saved in English (reports/exports group by these); the label shown is
// expense.categories.<key>.
const CATEGORIES = {
  "Rig Day Rate": "rigDayRate",
  Labor: "labor",
  "Fuel / Diesel": "fuel",
  "Equipment Rental": "equipmentRental",
  "Chemicals / Mud": "chemicalsMud",
  "Catering / Camp": "catering",
  Transportation: "transportation",
  "Safety Equipment": "safetyEquipment",
  "Fishing / Remedial": "fishing",
  Cementing: "cementing",
  Wireline: "wireline",
  Trucking: "trucking",
  "Water Disposal": "waterDisposal",
  Chemicals: "chemicals",
  Miscellaneous: "misc",
  Other: "other",
};

const EMPTY_ITEM = {
  description: "",
  amount: "",
  vendor: "",
  category: "",
  codeId: "",
  notes: "",
};

export default function NewExpense({
  token,
  wells,
  defaultWellId,
  onBack,
  onSaved,
}) {
  const isMobile = useIsMobile();
  const [wellId, setWellId] = useState(defaultWellId || "");
  const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
  const [lineItems, setLineItems] = useState([{ ...EMPTY_ITEM }]);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // Optional AFE tag — open AFEs on the selected well only (the backend
  // rejects other wells' or closed AFEs anyway).
  const { t, i18n } = useTranslation();
  const [openAfes, setOpenAfes] = useState([]);
  const [afeId, setAfeId] = useState("");
  const [afeCategory, setAfeCategory] = useState("");
  // Division P&L tag (orgs with rig services / hydro-testing only).
  const [divTag, setDivTag] = useState(EMPTY_DIVISION_TAG);
  const [divisionsOn, setDivisionsOn] = useState(false);

  useEffect(() => {
    setAfeId("");
    setOpenAfes([]);
    setDivTag((d) => ({ ...d, jobId: "" }));
    if (!wellId) return;
    axios
      .get(`${process.env.REACT_APP_API_URL}/api/afes`, {
        headers: { Authorization: "Bearer " + token },
        params: { wellId, status: "open" },
      })
      .then((res) => setOpenAfes(res.data || []))
      .catch(() => setOpenAfes([]));
  }, [wellId, token]);

  const total = lineItems.reduce(
    (sum, item) => sum + (Number(item.amount) || 0),
    0,
  );

  function addItem() {
    setLineItems([...lineItems, { ...EMPTY_ITEM }]);
  }

  function removeItem(idx) {
    setLineItems(lineItems.filter((_, i) => i !== idx));
  }

  function updateItem(idx, field, value) {
    const updated = [...lineItems];
    updated[idx] = { ...updated[idx], [field]: value };
    setLineItems(updated);
  }

  async function handleSubmit() {
    if (!wellId || !date) {
      setError(t("expense.errWellDate"));
      return;
    }
    if (lineItems.length === 0) {
      setError(t("expense.errNoItems"));
      return;
    }
    const invalid = lineItems.find((item) => !item.description || !item.amount);
    if (invalid) {
      setError(t("expense.errItem"));
      return;
    }
    const splitErr = divisionsOn ? splitProblem(divTag, total, t) : "";
    if (splitErr) {
      setError(splitErr);
      return;
    }

    setError("");
    setLoading(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/expenses`,
        { wellId, date, lineItems, notes, afeId: afeId || null, afeCategory: afeId ? afeCategory : "", ...divisionBody(divTag, divisionsOn) },
        {
          headers: {
            Authorization: "Bearer " + token,
            "Content-Type": "application/json",
          },
        },
      );
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || t("expense.saveFailed"));
    } finally {
      setLoading(false);
    }
  }

  const cyan = "#00f5ff";
  const border = "rgba(0,245,255,0.15)";
  const card = "rgba(0,245,255,0.03)";

  const inputStyle = {
    width: "100%",
    backgroundColor: "rgba(0,245,255,0.05)",
    color: "white",
    border: "1px solid rgba(0,245,255,0.2)",
    borderRadius: "6px",
    padding: "8px 12px",
    fontSize: "13px",
    outline: "none",
    boxSizing: "border-box",
    fontFamily: "'Segoe UI', sans-serif",
  };

  const labelStyle = {
    color: cyan,
    fontSize: "9px",
    letterSpacing: "2px",
    textTransform: "uppercase",
    display: "block",
    marginBottom: "4px",
  };

  const categoryOptions = (
    <>
      <option value="">{t("expense.categoryPlaceholder")}</option>
      {Object.entries(CATEGORIES).map(([value, key]) => (
        <option key={value} value={value}>
          {t(`expense.categories.${key}`)}
        </option>
      ))}
    </>
  );

  return (
    <div
      style={{
        minHeight: "100vh",
        backgroundColor: "#020817",
        fontFamily: "'Segoe UI', sans-serif",
      }}
    >
      {/* NAV */}
      <nav
        style={{
          backgroundColor: "rgba(0,0,0,0.6)",
          borderBottom: `1px solid ${border}`,
          padding: isMobile ? "14px 16px" : "14px 28px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backdropFilter: "blur(10px)",
        }}
      >
        <div
          style={{
            fontSize: "22px",
            fontWeight: "900",
            letterSpacing: "4px",
            background: "linear-gradient(135deg, #00f5ff, #0080ff)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          RIGIQ
        </div>
        <button
          onClick={onBack}
          style={{
            color: "rgba(0,245,255,0.5)",
            background: "none",
            border: `1px solid ${border}`,
            cursor: "pointer",
            fontSize: "11px",
            padding: "5px 12px",
            minHeight: "36px",
            borderRadius: "4px",
            letterSpacing: "1px",
          }}
        >
          {t("expense.back")}
        </button>
      </nav>

      <main style={{ padding: isMobile ? "20px 16px" : "28px", maxWidth: "900px" }}>
        <h2
          style={{
            fontSize: "22px",
            fontWeight: "800",
            letterSpacing: "3px",
            color: "white",
            marginBottom: "4px",
          }}
        >
          {t("expense.title")}
        </h2>
        <p
          style={{
            color: "rgba(0,245,255,0.4)",
            fontSize: "11px",
            letterSpacing: "3px",
            marginBottom: "28px",
          }}
        >
          {t("expense.subtitle")}
        </p>

        {/* WELL + DATE */}
        <div
          style={{
            backgroundColor: card,
            border: `1px solid ${border}`,
            borderRadius: "10px",
            padding: "20px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              color: cyan,
              fontSize: "10px",
              fontWeight: "700",
              letterSpacing: "3px",
              marginBottom: "16px",
            }}
          >
            {t("expense.section1")}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: "16px",
            }}
          >
            <div>
              <label style={labelStyle} htmlFor="expense-well">
                {t("expense.well")}
              </label>
              <select
                id="expense-well"
                value={wellId}
                onChange={(e) => setWellId(e.target.value)}
                style={{ ...inputStyle, minHeight: "44px" }}
              >
                <option value="">{t("expense.selectWell")}</option>
                {wells.map((w) => (
                  <option key={w._id || w.id} value={w._id || w.id}>
                    {w.well_name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={labelStyle} htmlFor="expense-date">
                {t("expense.date")}
              </label>
              <input
                id="expense-date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                style={{ ...inputStyle, minHeight: "44px" }}
              />
            </div>
          </div>
        </div>

        {/* AFE TAG (optional) — only when the well has open AFEs */}
        {openAfes.length > 0 && (
          <div
            data-testid="expense-afe-section"
            style={{
              backgroundColor: card,
              border: `1px solid ${border}`,
              borderRadius: "10px",
              padding: "20px",
              marginBottom: "16px",
            }}
          >
            <div style={{ color: cyan, fontSize: "10px", fontWeight: "700", letterSpacing: "3px", marginBottom: "16px" }}>
              {t("afe.tag.section")}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "16px" }}>
              <div>
                <label style={labelStyle} htmlFor="expense-afe">
                  {t("afe.tag.afe")}
                </label>
                <select
                  id="expense-afe"
                  value={afeId}
                  onChange={(e) => setAfeId(e.target.value)}
                  style={{ ...inputStyle, minHeight: "44px" }}
                >
                  <option value="">{t("afe.tag.none")}</option>
                  {openAfes.map((a) => (
                    <option key={a._id} value={a._id}>
                      {a.afeNumber}
                      {a.description ? ` — ${a.description}` : ""}
                    </option>
                  ))}
                </select>
              </div>
              {afeId && (
                <div>
                  <label style={labelStyle} htmlFor="expense-afe-category">
                    {t("afe.tag.category")}
                  </label>
                  <select
                    id="expense-afe-category"
                    value={afeCategory}
                    onChange={(e) => setAfeCategory(e.target.value)}
                    style={{ ...inputStyle, minHeight: "44px" }}
                  >
                    <option value="">{t("afe.tag.autoCategory")}</option>
                    {AFE_CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {t(`afe.categories.${c}`)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </div>
        )}

        <ExpenseDivisionPicker
          token={token}
          wellId={wellId}
          total={total}
          value={divTag}
          onChange={setDivTag}
          onEnabled={setDivisionsOn}
          inputStyle={inputStyle}
          labelStyle={labelStyle}
          isMobile={isMobile}
        />

        {/* LINE ITEMS */}
        <div
          style={{
            backgroundColor: card,
            border: `1px solid ${border}`,
            borderRadius: "10px",
            padding: "20px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: "10px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                color: cyan,
                fontSize: "10px",
                fontWeight: "700",
                letterSpacing: "3px",
              }}
            >
              {t("expense.section2")}
            </div>
            <button
              onClick={addItem}
              style={{
                background: "linear-gradient(135deg, #00f5ff, #0080ff)",
                color: "#020817",
                fontWeight: "700",
                fontSize: "10px",
                letterSpacing: "2px",
                padding: "6px 16px",
                minHeight: "36px",
                borderRadius: "4px",
                border: "none",
                cursor: "pointer",
              }}
            >
              {t("expense.addItem")}
            </button>
          </div>

          {/* Column Headers */}
          {!isMobile && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1.5fr 1fr 1.5fr 1fr 28px",
                gap: "8px",
                marginBottom: "8px",
                padding: "6px 8px",
                backgroundColor: "rgba(0,245,255,0.06)",
                borderRadius: "4px",
              }}
            >
              {["description", "vendor", "amount", "category", "code", ""].map((h) => (
                <div
                  key={h || "x"}
                  style={{
                    color: cyan,
                    fontSize: "8px",
                    letterSpacing: "2px",
                    fontWeight: "700",
                  }}
                >
                  {h ? t(`expense.cols.${h}`) : ""}
                </div>
              ))}
            </div>
          )}

          {lineItems.map((item, idx) =>
            isMobile ? (
              <div
                key={idx}
                style={{
                  backgroundColor: "rgba(0,245,255,0.04)",
                  border: `1px solid ${border}`,
                  borderRadius: "8px",
                  padding: "12px",
                  marginBottom: "10px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span
                    style={{
                      color: cyan,
                      fontSize: "9px",
                      letterSpacing: "2px",
                      fontWeight: "700",
                    }}
                  >
                    {t("expense.lineItem", { n: idx + 1 })}
                  </span>
                  <button
                    onClick={() => removeItem(idx)}
                    aria-label={t("expense.removeAria", { n: idx + 1 })}
                    style={{
                      background: "none",
                      border: "1px solid rgba(255,68,102,0.4)",
                      color: "rgba(255,68,102,0.7)",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px",
                      padding: "4px 8px",
                      minHeight: "36px",
                    }}
                  >
                    {t("expense.remove")}
                  </button>
                </div>
                <input
                  value={item.description}
                  onChange={(e) =>
                    updateItem(idx, "description", e.target.value)
                  }
                  placeholder={t("expense.descriptionPlaceholderMobile")}
                  style={inputStyle}
                />
                <input
                  value={item.vendor}
                  onChange={(e) => updateItem(idx, "vendor", e.target.value)}
                  placeholder={t("expense.vendorPlaceholder")}
                  style={inputStyle}
                />
                <input
                  type="number"
                  inputMode="decimal"
                  value={item.amount}
                  onChange={(e) => updateItem(idx, "amount", e.target.value)}
                  placeholder={t("expense.amountPlaceholder")}
                  style={inputStyle}
                  min="0"
                  step="0.01"
                />
                <select
                  value={item.category}
                  onChange={(e) => updateItem(idx, "category", e.target.value)}
                  style={inputStyle}
                >
                  {categoryOptions}
                </select>
                <input
                  value={item.codeId}
                  onChange={(e) => updateItem(idx, "codeId", e.target.value)}
                  placeholder={t("expense.codePlaceholder")}
                  style={inputStyle}
                />
                <input
                  value={item.notes}
                  onChange={(e) => updateItem(idx, "notes", e.target.value)}
                  placeholder={t("expense.itemNotesPlaceholder")}
                  style={{
                    ...inputStyle,
                    fontSize: "11px",
                    backgroundColor: "rgba(0,245,255,0.02)",
                    color: "rgba(255,255,255,0.5)",
                  }}
                />
              </div>
            ) : (
              <div key={idx}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1.5fr 1fr 1.5fr 1fr 28px",
                    gap: "8px",
                    marginBottom: "4px",
                    alignItems: "center",
                  }}
                >
                  <input
                    value={item.description}
                    onChange={(e) =>
                      updateItem(idx, "description", e.target.value)
                    }
                    placeholder={t("expense.descriptionPlaceholder")}
                    style={inputStyle}
                  />
                  <input
                    value={item.vendor}
                    onChange={(e) => updateItem(idx, "vendor", e.target.value)}
                    placeholder={t("expense.vendorPlaceholder")}
                    style={inputStyle}
                  />
                  <input
                    type="number"
                    value={item.amount}
                    onChange={(e) => updateItem(idx, "amount", e.target.value)}
                    placeholder="0.00"
                    style={inputStyle}
                    min="0"
                    step="0.01"
                  />
                  <select
                    value={item.category}
                    onChange={(e) =>
                      updateItem(idx, "category", e.target.value)
                    }
                    style={inputStyle}
                  >
                    {categoryOptions}
                  </select>
                  <input
                    value={item.codeId}
                    onChange={(e) => updateItem(idx, "codeId", e.target.value)}
                    placeholder={t("expense.codePlaceholder")}
                    style={inputStyle}
                  />
                  <button
                    onClick={() => removeItem(idx)}
                    aria-label={t("expense.removeAria", { n: idx + 1 })}
                    style={{
                      background: "none",
                      border: "1px solid rgba(255,68,102,0.4)",
                      color: "rgba(255,68,102,0.7)",
                      borderRadius: "4px",
                      cursor: "pointer",
                      fontSize: "12px",
                      padding: "4px 6px",
                    }}
                  >
                    ✕
                  </button>
                </div>
                {/* Notes row */}
                <div style={{ marginBottom: "10px", paddingLeft: "4px" }}>
                  <input
                    value={item.notes}
                    onChange={(e) => updateItem(idx, "notes", e.target.value)}
                    placeholder={t("expense.itemNotesPlaceholder")}
                    style={{
                      ...inputStyle,
                      fontSize: "11px",
                      backgroundColor: "rgba(0,245,255,0.02)",
                      color: "rgba(255,255,255,0.5)",
                    }}
                  />
                </div>
              </div>
            ),
          )}

          {lineItems.length === 0 && (
            <div
              style={{
                textAlign: "center",
                padding: "30px",
                color: "rgba(0,245,255,0.3)",
                fontSize: "12px",
                letterSpacing: "3px",
              }}
            >
              {t("expense.noItems")}
            </div>
          )}
        </div>

        {/* REPORT NOTES */}
        <div
          style={{
            backgroundColor: card,
            border: `1px solid ${border}`,
            borderRadius: "10px",
            padding: "20px",
            marginBottom: "16px",
          }}
        >
          <div
            style={{
              color: cyan,
              fontSize: "10px",
              fontWeight: "700",
              letterSpacing: "3px",
              marginBottom: "12px",
            }}
          >
            {t("expense.section3")}
          </div>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            placeholder={t("expense.notesPlaceholder")}
            style={{ ...inputStyle, resize: "vertical" }}
          />
        </div>

        {/* TOTAL */}
        <div
          style={{
            backgroundColor: "rgba(0,245,255,0.08)",
            border: "1px solid rgba(0,245,255,0.3)",
            borderRadius: "10px",
            padding: "20px",
            marginBottom: "20px",
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div>
            <div
              style={{
                color: "rgba(0,245,255,0.5)",
                fontSize: "10px",
                letterSpacing: "3px",
                marginBottom: "4px",
              }}
            >
              {t("expense.totalLabel")}
            </div>
            <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "11px" }}>
              {t("expense.itemCount", { count: lineItems.length })}
            </div>
          </div>
          <div style={{ color: cyan, fontSize: isMobile ? "26px" : "32px", fontWeight: "900" }}>
            $
            {total.toLocaleString(i18n.language === "es" ? "es-US" : "en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        </div>

        {error && <ErrorBanner message={error} />}

        {/* BUTTONS */}
        <div style={{ display: "flex", gap: "12px" }}>
          <button
            onClick={onBack}
            style={{
              flex: 1,
              background: "none",
              border: `1px solid ${border}`,
              color: "rgba(0,245,255,0.5)",
              fontWeight: "700",
              fontSize: "11px",
              letterSpacing: "2px",
              padding: "12px",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            {t("expense.cancel")}
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading}
            style={{
              flex: 2,
              background: loading
                ? "rgba(0,245,255,0.1)"
                : "linear-gradient(135deg, #00f5ff, #0080ff)",
              color: loading ? "rgba(0,245,255,0.5)" : "#020817",
              fontWeight: "700",
              fontSize: "11px",
              letterSpacing: isMobile ? "1px" : "3px",
              padding: "12px",
              borderRadius: "6px",
              border: "none",
              cursor: loading ? "not-allowed" : "pointer",
              boxShadow: loading ? "none" : "0 0 20px rgba(0,245,255,0.3)",
            }}
          >
            {loading ? t("expense.saving") : t("expense.submit")}
          </button>
        </div>
      </main>
    </div>
  );
}
